import React from 'react';
import { Box, Typography, Modal as LegoModal, Button, Loader } from '@lucid/lego';
import styles from './Modal.module.scss';

export type ModalVariant = 'success' | 'sending' | 'sent';

type CheckoutModalProps = {
  /** Test ID for the modal */
  dataTestId: string;
  /** Whether the modal is open */
  isOpen: boolean;
  /** Handler for closing the modal */
  onClose: () => void;
  /** Theme for the modal */
  theme?: 'light' | 'dark';
  /** Variant determines the modal's appearance and content */
  variant: ModalVariant;
  /** Custom message to display (for success variant) */
  message?: string;
  /** Whether to show loader (for sending variant) */
  showLoader?: boolean;
};

/**
 * Unified modal component for checkout operations
 * Handles success messages, sending states, and sent confirmations
 * @component
 * @param {CheckoutModalProps} props - Component props
 * @returns {JSX.Element} The CheckoutModal component
 * @example
 * ```tsx
 * // Success modal
 * <CheckoutModal
 *   dataTestId="save-quote-modal"
 *   isOpen={isModalOpen}
 *   onClose={handleClose}
 *   variant="success"
 *   message="Quote saved successfully"
 * />
 *
 * // Sending docusign modal
 * <CheckoutModal
 *   dataTestId="sending-docusign-modal"
 *   isOpen={isModalOpen}
 *   onClose={handleClose}
 *   variant="sending"
 *   showLoader={true}
 * />
 *
 * // Docusign sent modal
 * <CheckoutModal
 *   dataTestId="docusign-sent-modal"
 *   isOpen={isModalOpen}
 *   onClose={handleClose}
 *   variant="sent"
 * />
 * ```
 */
const CheckoutModal: React.FC<CheckoutModalProps> = ({
  dataTestId,
  isOpen,
  onClose,
  theme = 'light',
  variant,
  message = 'The quotation has been successfully saved to the prospect.',
  showLoader = false,
}) => {
  // Determine modal title based on variant
  const getTitle = () => {
    switch (variant) {
      case 'success':
        return 'Saved to Prospect';
      case 'sending':
        return 'Sending Docusign';
      case 'sent':
        return 'Docusign Sent';
      default:
        return '';
    }
  };

  // Determine modal content based on variant
  const getContent = () => {
    switch (variant) {
      case 'success':
        return (
          <Typography variant="body" size="medium" theme={theme} color="white">
            {message}
          </Typography>
        );
      case 'sending':
        return (
          <>
            <Typography variant="body" size="medium" theme={theme} color="gray-5">
              Please wait, sending Docusign to customer and holding vehicle.
            </Typography>
            {showLoader && (
              <Box className={styles.loaderContainer}>
                <Loader size={32} />
              </Box>
            )}
          </>
        );
      case 'sent':
        return (
          <>
            <Typography variant="body" size="medium" theme={theme} color="gray-5">
              Docusign has been successfully sent to customer.
            </Typography>
            <Typography variant="body" size="medium" theme={theme} color="gray-5">
              Vehicle has been put on hold.
            </Typography>
          </>
        );
      default:
        return null;
    }
  };

  // Prevent closing during sending state
  const closeOnOverlayClick = variant !== 'sending';
  const closeOnEscape = variant !== 'sending';

  return (
    <LegoModal
      dataTestId={dataTestId}
      isOpen={isOpen}
      onClose={onClose}
      title={getTitle()}
      theme={theme}
      size="small"
      closeOnOverlayClick={closeOnOverlayClick}
      closeOnEscape={closeOnEscape}
      footer={
        <Box className={styles.footer}>
          <Button
            label="CLOSE"
            type="primary"
            color="neutral"
            theme={theme}
            size="medium"
            handleClick={onClose}
            dataTestId={`${dataTestId}-close-button`}
            gaComponent={`${variant}-modal-close`}
            isFluid={variant !== 'success'}
          />
        </Box>
      }
    >
      <Box className={styles.content}>{getContent()}</Box>
    </LegoModal>
  );
};

CheckoutModal.displayName = 'CheckoutModal';

export default CheckoutModal;
